import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Stack, Text } from '@wapharma/design-system';
import { STAFF_EMAIL_DOMAIN } from '@wapharma/shared';
import { validatePasswordResetToken } from '../auth/passwordReset';
import {
  getStoredPasswordResetEmail,
  passwordResetToastMessage,
  submitPasswordResetEmail,
} from '../auth/password-reset-email';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import authStyles from '../components/auth/auth-layout.module.css';
import { StepSwitch } from '../components/StepSwitch';
import { useToast } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';

type Step = 'email' | 'code';

function staffLocalFromEmail(email: string): string {
  const v = email.trim();
  const at = v.indexOf('@');
  return (at >= 0 ? v.slice(0, at) : v).toLowerCase();
}

function toStaffEmail(localPart: string): string {
  return `${localPart.trim().toLowerCase()}@${STAFF_EMAIL_DOMAIN}`;
}

function validateStaffLocal(value: string): string | undefined {
  const v = value.trim();
  if (!v) return "L'identifiant est requis.";
  if (v.includes('@') || /\s/.test(v) || !/^[a-zA-Z0-9._%+-]+$/.test(v)) {
    return 'Identifiant invalide.';
  }
  return undefined;
}

export function ForgotPasswordPage() {
  const { showToast, clearToasts } = useToast();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [username, setUsername] = useState(() => staffLocalFromEmail(getStoredPasswordResetEmail()));
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('email');
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const resetInFlightRef = useRef(false);

  const resolvedEmail = toStaffEmail(username);

  const emailError = useMemo(() => {
    if (!emailTouched && !submitAttempted) return undefined;
    return validateStaffLocal(username);
  }, [username, emailTouched, submitAttempted]);

  const stepIndex = step === 'code' ? 1 : 0;

  useEffect(() => () => clearToasts(), [clearToasts]);

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (resetInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    const err = validateStaffLocal(username);
    if (err) return;

    resetInFlightRef.current = true;
    setPending(true);
    try {
      const result = await submitPasswordResetEmail(resolvedEmail);
      showToast(passwordResetToastMessage(result), {
        tone: result === 'throttled' ? 'error' : 'success',
      });
      setCode('');
      setStep('code');
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      resetInFlightRef.current = false;
      setPending(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    const token = code.trim();
    if (!/^\d{6}$/.test(token)) {
      showToast('Code invalide. Saisissez les 6 chiffres.', { tone: 'error' });
      return;
    }

    setPending(true);
    try {
      await validatePasswordResetToken(token);
      navigate(`/reset-password?token=${encodeURIComponent(token)}`);
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      setPending(false);
    }
  };

  const resendCode = async () => {
    if (resetInFlightRef.current || pending) return;
    const err = validateStaffLocal(username);
    if (err) {
      showToast(err, { tone: 'error' });
      return;
    }

    resetInFlightRef.current = true;
    setPending(true);
    try {
      const result = await submitPasswordResetEmail(resolvedEmail);
      showToast(passwordResetToastMessage(result), {
        tone: result === 'throttled' ? 'error' : 'success',
      });
      setCode('');
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      resetInFlightRef.current = false;
      setPending(false);
    }
  };

  return (
    <AdminAuthLayout
      brandTitle="Réinitialisation"
      brandBody="Recevez un code par e-mail pour définir un nouveau mot de passe personnel."
    >
      <Stack gap="xl">
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
          <StepSwitch index={stepIndex} mode="panels">
            <Stack gap={0}>
              <div className={authStyles.formHeaderBand}>
                <div className={authStyles.formHeading}>
                  <Text ff="display" size="lg" fw={500} c="primary" component="div">
                    Mot de passe oublié
                  </Text>
                  <Text size="sm" c="dimmed" ff="body">
                    Indiquez votre identifiant pour recevoir un code de réinitialisation.
                  </Text>
                </div>
              </div>
              <Stack gap="lg" className={authStyles.authStepBody}>
                <form onSubmit={(e) => void submitEmail(e)} noValidate>
                  <Stack gap="md">
                    <Input
                      label="Identifiant"
                      name="username"
                      type="text"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="username"
                      placeholder="prenom.nom"
                      value={username}
                      suffix={`@${STAFF_EMAIL_DOMAIN}`}
                      error={emailError}
                      autoFocus
                      tabIndex={step === 'email' ? undefined : -1}
                      onChange={(ev) => {
                        let next = ev.currentTarget.value;
                        const at = next.indexOf('@');
                        if (at >= 0) next = next.slice(0, at);
                        setUsername(next.replace(/\s/g, ''));
                        setEmailTouched(true);
                      }}
                      onBlur={() => setEmailTouched(true)}
                    />
                    <Button type="submit" disabled={pending} className={authStyles.submitBtn}>
                      {pending ? 'Envoi…' : 'Envoyer le code'}
                    </Button>
                    <div className={authStyles.authFormFooter}>
                      <Link
                        to="/login"
                        className={authStyles.authNavLink}
                        tabIndex={step === 'email' ? undefined : -1}
                      >
                        Retour à la connexion
                      </Link>
                    </div>
                  </Stack>
                </form>
              </Stack>
            </Stack>

            <Stack gap={0}>
              <div className={authStyles.formHeaderBand}>
                <div className={authStyles.formHeading}>
                  <Text ff="display" size="lg" fw={500} c="primary" component="div">
                    Mot de passe oublié
                  </Text>
                  <Text size="sm" c="dimmed" ff="body">
                    Saisissez le code à 6 chiffres reçu par e-mail.
                  </Text>
                </div>
              </div>
              <Stack gap="lg" className={authStyles.authStepBody}>
                <form onSubmit={(e) => void submitCode(e)} noValidate>
                  <Stack gap="md">
                    <Input
                      label="Code reçu par e-mail"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      value={code}
                      autoFocus={step === 'code'}
                      tabIndex={step === 'code' ? undefined : -1}
                      onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                    <Button type="submit" disabled={pending} className={authStyles.submitBtn}>
                      {pending ? 'Vérification…' : 'Continuer'}
                    </Button>
                    <div className={authStyles.authSecondaryActions}>
                      <button
                        type="button"
                        className={authStyles.authActionLink}
                        disabled={pending || !username.trim()}
                        tabIndex={step === 'code' ? undefined : -1}
                        onClick={() => void resendCode()}
                      >
                        Renvoyer le code
                      </button>
                      <span className={authStyles.authLinkSeparator} aria-hidden>
                        ·
                      </span>
                      <button
                        type="button"
                        className={authStyles.authActionLink}
                        disabled={pending}
                        tabIndex={step === 'code' ? undefined : -1}
                        onClick={() => {
                          setStep('email');
                          setCode('');
                          setSubmitAttempted(false);
                        }}
                      >
                        Utiliser un autre identifiant
                      </button>
                    </div>
                    <div className={authStyles.authFormFooter}>
                      <Link
                        to="/login"
                        className={authStyles.authNavLink}
                        tabIndex={step === 'code' ? undefined : -1}
                      >
                        Retour à la connexion
                      </Link>
                    </div>
                  </Stack>
                </form>
              </Stack>
            </Stack>
          </StepSwitch>
        </Card>

        <Text size="xs" c="dimmed" ta="center" ff="body" className={authStyles.mobileFooter}>
          © {year} W.A. Pharma · Réservé au personnel autorisé
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
